import React, {useEffect, useRef, useState} from "react";
import { useLoaderData } from "react-router-dom";
import IDE from "../IDE/IDE";
import Header from "../IDE/Header";
import {Button, Card, ConfigProvider, Steps, Tour, TourProps, Typography} from "antd";
import {RedoOutlined, SelectOutlined, SyncOutlined, UndoOutlined} from "@ant-design/icons";
const { Text, Paragraph } = Typography;

import redX from "../images/redX.png";
import RoundVisual from "../IDE/Round";
import {EventService} from "../services/eventService";

// @ts-ignore
export async function loader({ params }) {
    const userId = params.userId;
    return { userId };
}

const initialText = `Gauge Swatch
This swatch is knit back and forth in a 1-by-1 rib.

Gauge: 20 stitches by 20 rows

~ Knitting Instructions ~
Cast On 24 stitches.

Round 1: (k1, p1) repeat 12 times.
Round 2: (p1, k1) repeat 12 times.

Round 3: (k1, p1) repeat 12 times.
Round 4: (p1, k1) repeat 12 times.

Repeat Rounds 3 and 4, 4 more times.
`

export default function TaskOne() {
    const eventService = new EventService();

    // @ts-ignore
    const { userId } = useLoaderData();

    // Element References
    let cardRef = useRef(null);
    let progressRef = useRef(null);
    let rowInspectorRef = useRef(null);

    // Timer
    const [timeRemaining, setTimeRemaining] = useState("7 minutes");
    useEffect(() => {
        // Send Initial Event
        const date = new Date();
        eventService.saveEvent(userId, "1", {
            id: date.getTime(),
            type: "TASK_START",
            time: date.toString(),
            value: {
                windowHeight: window.innerHeight,
                widowWidth: window.innerWidth
            }
        });

        setTimeout(() => {
            setTimeRemaining("6 minutes");
        }, 60 * 1000 * 1);

        setTimeout(() => {
            setTimeRemaining("5 minutes");
        }, 60 * 1000 * 2);

        setTimeout(() => {
            setTimeRemaining("4 minutes");
        }, 60 * 1000 * 3);

        setTimeout(() => {
            setTimeRemaining("3 minutes");
        }, 60 * 1000 * 4);

        setTimeout(() => {
            setTimeRemaining("2 minutes");
        }, 60 * 1000 * 5);

        setTimeout(() => {
            setTimeRemaining("1 minute");
        }, 60 * 1000 * 6);
    }, []);

    const saveStepChange = (step: string) => {
        const date = new Date();
        eventService.saveEvent(userId, "1", {
            id: date.getTime(),
            type: "STEP_CHANGE",
            time: date.toString(),
            value: step
        });
    }

    // Progress Bar and Card Steps
    const [current, setCurrent] = useState(0);
    const nextStep = () => {
        const nextStep = current + 1;
        saveStepChange(nextStep.toString());
        setCurrent(nextStep);
    };
    const prevStep = () => {
        const nextStep = current - 1
        saveStepChange(nextStep.toString());
        setCurrent(nextStep);
    };
    const onChangeStep = (value: number) => {
        saveStepChange(value.toString());
        setCurrent(value);
    };

    // Card Steps
    // Card One Tours
    const [rowInspectorOpen, setRowInspectorOpen] = useState<boolean>(false);
    const rowInspectorTour = [
        {
            title: 'Row Inspector',
            target: () => rowInspectorRef.current
        }
    ]

    const steps = [
        {
            shortTitle: 'Overview',
            title: 'Overview: Learning the layout',
            task: <>
                <Paragraph>
                    StitchUp is a pattern editing tool. It has two parts:
                </Paragraph>
                <Paragraph>
                    <ol>
                        <li key="overview-2">
                            A <Text strong>Pattern Visual</Text> where you can preview what the pattern will look like.
                            Located below the <Text strong>Steps</Text>.
                        </li>
                        <li key="overview-1">
                            A <Text strong>Pattern Editor</Text> where you will write knitting instructions.
                            Located on the right-hand side.
                        </li>
                    </ol>
                </Paragraph>
            </>
        },
        {
            shortTitle: 'Edit',
            title: 'Edit: Try updating the pattern',
            task: <>
                <Paragraph>Find <Text code>Line 10</Text> in the Pattern Editor, which has the text
                    <Text code>Round 2: (p1, k1) repeat 12 times.</Text>.
                </Paragraph>
                <Paragraph>
                    Let's update <Text code>Line 10</Text>, so that this knitting instruction is only repeated 10 times (not 12 times).
                </Paragraph>
                <Paragraph>Update the end of <Text code>Line 10</Text> to say:
                    <Text code>Round 2: (p1, k1) repeat <Text strong>10 times</Text>.</Text>
                </Paragraph>
                <Paragraph>We have now introduced an error into our pattern. In the next couple of steps, we will use
                    tools to observe the error, and then we will fix it. Time to click the "Next" button!
                </Paragraph>
            </>
        },
        {
            shortTitle: 'Error',
            title: 'Observing the Error',
            task: <>
                <Paragraph>
                    You should see red highlighted rows,
                    <div style={{display: "inline-block"}}>
                        <RoundVisual
                            index={1}
                            userId={"1"}
                            taskId={"1"}
                            hoverActive={false}
                            round={{
                                stitches: ["k", "p", "k"],
                                type: "Knit",
                                properties: {
                                    inc: 0,
                                    dec: 0,
                                    err: true,
                                    line: 0
                                }
                            }}
                            gauge={[]}
                            onMouseEnter={() => {}}/>
                    </div>, in the Pattern Visual and Red X's, <img alt="" src={redX} height={20} width={20}/>, in the Pattern Editor
                    beside <Text code>Line 10</Text> and <Text code>Line 12</Text>.
                </Paragraph>
                <Paragraph>
                    Hover your mouse over the Red X, <img alt="" src={redX} height={20} width={20}/>, in the
                    Pattern Editor and read the error message. The error message should read: <Text code>Error: Wrong number of stitches</Text>.
                </Paragraph>
                <Paragraph>
                    This error appeared because on <Text code>Line 7</Text>, we <Text code>Cast On 24 stitches</Text>, but on <Text code>Line 10</Text>
                    we only work 20 stitches (<Text code>Round 2: (k1, p1) 10 times</Text>).
                </Paragraph>
            </>
        },
        {
            shortTitle: 'Inspect',
            title: 'Inspect: Inspecting the rows',
            task: <>
                <Paragraph>
                    The <Text strong>Pattern Visual</Text> shows this error too, as some rows of stitches are highlighted
                    red,
                    <div style={{display: "inline-block"}}>
                        <RoundVisual
                            index={1}
                            userId={"1"}
                            taskId={"1"}
                            hoverActive={false}
                            round={{
                                stitches: ["k", "p", "k"],
                                type: "Knit",
                                properties: {
                                    inc: 0,
                                    dec: 0,
                                    err: true,
                                    line: 0
                                }
                            }}
                            gauge={[]}
                            onMouseEnter={() => {}}/>
                    </div>.
                    These rows have a different number of stitches, when compared to the row below.
                </Paragraph>
                <Paragraph>
                    Click the
                    <ConfigProvider
                        theme={{
                            token: {
                                colorPrimary: '#E27D60',
                            },
                        }}
                    >
                        <Button
                            style={{margin: "2px"}}
                            type="primary"
                            icon={<SelectOutlined />}
                            onClick={() => setRowInspectorOpen(true)}
                            size="small">Row Inspector
                        </Button>
                    </ConfigProvider> button above the Pattern Visual and move your mouse over the rows of stitches. The rows
                    of stitches should <Text className="visual-hover-colour">highlighted yellow</Text> as you move your mouse.
                </Paragraph>
                <Paragraph>
                    Also notice that as you move your mouse, text in the <Text strong>Pattern Editor</Text> becomes <Text className="text-hover-colour">highlighted blue</Text>.
                </Paragraph>
                <Tour
                    open={rowInspectorOpen}
                    onClose={() => setRowInspectorOpen(false)}
                    steps={rowInspectorTour}
                    mask={true}
                />

            </>
        },
        {
            shortTitle: 'Fix',
            title: "Fix: Let's fix that error!",
            task: <>
                <Paragraph>
                    Using the Row Inspector, highlight the red rows of stitches, and you should see <Text code>Line 10 (Round 2)</Text> and <Text code>Line 12 (Round 3)</Text> highlighted in the Pattern Editor.
                </Paragraph>
                <Paragraph>
                    Above the <Text strong>Pattern Editor</Text>, there are three buttons:
                    <ConfigProvider
                        theme={{
                            token: {
                                colorPrimary: '#E27D60',
                            },
                        }}
                    >
                        <Button
                            style={{margin: "2px"}}
                            type="primary"
                            icon={<UndoOutlined />}
                            size="small">Undo
                        </Button>
                        <Button
                            style={{margin: "2px"}}
                            type="primary"
                            icon={<RedoOutlined />}
                            size="small">Redo
                        </Button>
                        <Button
                            style={{margin: "2px"}}
                            type="primary"
                            icon={<SyncOutlined />}
                            size="small">Reset
                        </Button>
                    </ConfigProvider>.
                    These buttons allow you to undo and redo editing, or reset all your changes back to the beginning.
                </Paragraph>
                <Paragraph>
                    Try clicking either "Undo" or "Reset" to get back to the original state of the pattern!
                </Paragraph>
            </>
        },
        {
            shortTitle: 'Play',
            title: 'Play: experiment!',
            task: <>
                <Paragraph>
                    Now it's time to update the pattern!
                    Below is a list of tasks to try until your 7 minutes are up:
                </Paragraph>
                <Paragraph>
                    <Text italic>Note: the last step contains a list of supported knitting instructions.
                    </Text>
                </Paragraph>
                <Paragraph>
                    List of tasks to try:
                    <ul>
                        <li>Update the stitch pattern of the swatch. Make it all knit stitches, 2x2 rib,
                            or whatever feels fun!</li>
                        <li>Add decreases. This tool supports <Text code>k2tog</Text> (or knitting any
                            number of stitches together). For example, you could add
                            <Text code>Round 5: k24tog</Text>.</li>
                        <li>Lastly, experiment with anything else you'd like to try!</li>
                    </ul>
                </Paragraph>
            </>
        },
        {
            shortTitle: 'Inst.',
            title: 'Knitting Instructions: What is possible',
            task: <>
                <Paragraph>
                    The following is a list of knitting instructions that are supported by StitchUp:
                    <ul>
                        <li>
                            Gauge:<Text code>Gauge: 20 stitches by 20 rows</Text>
                        </li>
                        <li>
                            Casting on:<Text code>Cast On 24 stitches</Text>
                        </li>
                        <li>
                            Stitch Rounds:<Text code>Round 5: k1, p2, k1, p2</Text>
                        </li>
                        <li>
                            Repeating stitches within a round:<Text code>Round 6: (k1, p2) repeat 2 times</Text>
                        </li>
                        <li>
                            Repeating a round: <Text code>Repeat Round 6, 2 more times</Text>
                        </li>
                        <li>
                            Repeating multiple rounds: <Text code>Repeat Rounds 1, 2 and 3, 5 more times</Text>
                        </li>
                        <li>
                            Supported stitches: knit (<Text code>k1</Text>), purl (<Text code>p1</Text>), and decreases (<Text code>k2tog</Text>)
                        </li>
                    </ul>
                </Paragraph>
            </>
        }
    ];

    let actions = [];
    if (current < steps.length - 1) {
        actions.push(<Button type="primary" onClick={() => nextStep()} style={{ float: "right" }}>
            Next
        </Button>);
    }
    if (current > 0) {
        actions.push(<Button style={{ margin: '0 8px', float: "right" }} onClick={() => prevStep()}>
            Previous
        </Button>)
    }
    if (current === steps.length - 2){
        actions.push(<Text>Time Remaining: <Text strong>{timeRemaining}</Text></Text>)
    }

    const items = steps.map((item) => ({ key: item.shortTitle, title: item.shortTitle }));

    return (
        <>
            <Header
                taskId={"1"}
                userId={userId}
            ></Header>
            <IDE
                userId={userId}
                taskId="1"
                initialText={initialText}
                setRowInspectorRef={rowInspectorRef}
            >
                <ConfigProvider
                    theme={{
                        token: {
                            colorPrimary: '#41B3A3',
                        },
                    }}
                >
                    <h2>Steps: Task One</h2>
                    <div ref={progressRef}>
                        <Steps
                            size="small"
                            className="step-space"
                            current={current}
                            items={items}
                            onChange={onChangeStep}/>
                    </div>
                    <Card
                        className="card-space"
                        type="inner"
                        ref={cardRef}
                        size="small"
                        title={steps[current].title}
                        bodyStyle={{height: "240px", overflow: "scroll"}}>
                        {steps[current].task}
                        {actions}
                    </Card>
                </ConfigProvider>
            </IDE>
        </>
    );
}